import React, { useEffect, useState } from 'react';
import {
    Box,
    IconButton,
    Typography,
    TextField,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Button,
    Divider,
    InputLabel,
    FormControl,
    Autocomplete,
    CircularProgress,
    Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { getReview } from '../../redux/slices/reviewerSlice';
import { useParams } from 'react-router-dom';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import EastIcon from '@mui/icons-material/East';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import dayjs from 'dayjs';

// Generic filter definition
interface FilterOption {
    label: string;
    value: any;
}

export interface Filter {
    name: string;
    label: string;
    type: 'text' | 'select' | 'multiselect' | 'checkboxes' | 'autocomplete' | 'stages' | 'range' | 'dateRange';
    options?: FilterOption[];
    value: any;
    onChange: (value: any) => void;
    reset?: () => void;

    // autocomplete-specific props
    open?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    inputValue?: string;
    onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    getOptionLabel?: (option: any) => string;
    loading?: boolean;
    minValue?: number;
    maxValue?: number;
    placeholderMin?: any;
    placeholderMax?: any;
    onMinChange?: any;
    onMaxChange?: any;
    adornment?: any
    startDate?: any;
    endDate?: any;
    onStartDateChange?: any
    onEndDateChange?: any
    clear?: any


}

interface DrawerFiltersProps {
    filters: Filter[];
    onApply: () => void;
    onResetAll?: () => void;
    setOpenDrawerFilter?: (open: boolean) => void;
    stages?: {
        category: string; id: string; name: string; stage_type: string
    }[];
    disabled?: boolean;
}

const DrawerFilters: React.FC<DrawerFiltersProps> = ({
    filters,
    onApply,
    onResetAll,
    setOpenDrawerFilter,
    stages,
    disabled = false
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();
    const association_type = useSelector(
        (state: RootState) => state.projects.association_type
      );

    useEffect(() => {
        dispatch(getReview({ project_id: id || "", association_type: "COMPANY" }));
      }, [dispatch, id, association_type]);

    const handleClose = () => setOpenDrawerFilter?.(false);

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Filters</Typography>
                <IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider sx={{ my: 1 }} />

            {/* Filter List */}
            <Box sx={{ flex: 1, overflowY: 'auto', mt: 1, height: '100%' }}>
                {filters.map(filter => (
                    <Box key={filter.name} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontFamily: "montserrat" }}>{filter.label}:</Typography>
                        </Box>


                        {filter.type === 'stages' && (
                            <FormControl fullWidth size="small">
                                <Select
                                    value={filter.value}
                                    placeholder={filter.label}
                                    onChange={e => filter.onChange(e.target.value)}
                                >
                                    {stages?.map(s => {
                                        return (
                                            <MenuItem key={s.id} value={s.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                    <Typography variant="body2">{s.name}</Typography>
                                                    <Chip
                                                        size="small"
                                                        label={s.category || "Task"}
                                                        sx={{
                                                            fontSize: 11,
                                                            backgroundColor: s.stage_type === 'REVIEW' ? 'primary.main' : 'default',
                                                            color: s.stage_type === 'REVIEW' ? 'common.white' : 'text.primary',
                                                            border: '1px solid #ccc'
                                                        }}
                                                    />
                                                </Box>
                                            </MenuItem>
                                        )
                                    })}
                                </Select>
                            </FormControl>
                        )}


                        {filter.type === 'text' && (
                            <TextField
                                fullWidth
                                size="small"
                                value={filter.value}
                                onChange={e => filter.onChange(e.target.value)}
                            // placeholder={filter.label}
                            />
                        )}


                        {filter.type === 'select' && (
                            <FormControl fullWidth size="small">
                                {/* <InputLabel id={`${filter.name}-label`}>{filter.label}</InputLabel> */}
                                <Select
                                    labelId={`${filter.name}-label`}
                                    id={`${filter.name}-select`}
                                    value={filter.value}
                                    // label={filter.label}
                                    onChange={e => filter.onChange(e.target.value)}
                                >
                                    {filter.options?.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value} style={{ fontFamily: "montserrat" }}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}


                        {filter.type === 'multiselect' && (
                            <Select
                                fullWidth
                                size="small"
                                multiple
                                value={filter.value}
                                onChange={e => filter.onChange(e.target.value)}
                                // renderValue={selected => (selected as any[]).join(', ')}
                                renderValue={selected => {
                                    if (!Array.isArray(selected)) return '';
                                    return (selected as string[]).map(s => s.charAt(0).toUpperCase().replace("_", ' ') + s.slice(1).replace("_", ' ').toLowerCase()).join(', ').replace("_", ' ');
                                }}
                            >
                                {filter.options?.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        <Checkbox checked={(filter.value as any[]).includes(opt.value)} size='small' />
                                        <Typography style={{ fontFamily: "montserrat" }}>{opt.label}</Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        )}


                        {filter.type === 'checkboxes' &&
                            filter.options?.map(opt => (
                                <FormControlLabel
                                    key={opt.value}
                                    control={
                                        <Checkbox
                                            size='small'
                                            checked={filter.value.includes(opt.value)}
                                            onChange={() => filter.onChange(
                                                filter.value.includes(opt.value)
                                                    ? filter.value.filter((v: any) => v !== opt.value)
                                                    : [...filter.value, opt.value]
                                            )}
                                        />
                                    }
                                    label={<span>{opt.label}</span>}
                                />
                            ))}


                        {filter.type === 'autocomplete' && (
                            <Autocomplete
                                size="small"
                                fullWidth
                                open={filter.open}
                                onOpen={filter.onOpen}
                                onClose={filter.onClose}
                                autoSelect
                                options={filter.options || []}
                                value={filter.value}
                                getOptionLabel={filter.getOptionLabel!}
                                loading={filter.loading}
                                onChange={(e, v) => filter.onChange(v)}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        // placeholder={filter.label}
                                        value={filter.inputValue}
                                        onChange={filter.onInputChange}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {filter.loading && <CircularProgress size={20} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                            />
                        )}


                        {filter.type === 'range' && (
                            <Box sx={{ display: "flex", gap: 2 }}>
                                <TextField
                                    size="small"
                                    value={filter.minValue}
                                    placeholder={filter.placeholderMin || "Min"}
                                    onChange={e => filter.onMinChange(e.target.value)}
                                    InputProps={{
                                        endAdornment: filter.adornment ? <span style={{ color: "#888" }}>{filter.adornment}</span> : null,
                                        inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                                    }}
                                    style={{ width: "120px" }}
                                />
                                <TextField
                                    size="small"
                                    value={filter.maxValue}
                                    placeholder={filter.placeholderMax || "Max"}
                                    onChange={e => filter.onMaxChange(e.target.value)}
                                    InputProps={{
                                        endAdornment: filter.adornment ? <span style={{ color: "#888" }}>{filter.adornment}</span> : null,
                                        inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                                    }}
                                    style={{ width: "120px" }}
                                />
                            </Box>
                        )}

                        {filter.type === 'dateRange' && (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <MobileDatePicker
                                        value={filter.startDate ? dayjs(filter.startDate) : null}
                                        onChange={filter.onStartDateChange}
                                        format="DD-MM-YYYY"
                                        slotProps={{
                                            textField: { size: "small", placeholder: "Start Date" } as any,
                                        }}
                                    />
                                    <EastIcon sx={{ fontSize: "16px" }} />
                                    <MobileDatePicker
                                       value={filter.endDate ? dayjs(filter.endDate) : null}
                                        onChange={filter.onEndDateChange}
                                        format="DD-MM-YYYY"
                                        minDate={filter.startDate ? dayjs(filter.startDate) : undefined}
                                        slotProps={{
                                            textField: { size: "small", placeholder: "End Date" } as any,
                                        }}
                                    />
                                    <IconButton onClick={filter.clear} size="small">
                                        <HighlightOffIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </LocalizationProvider>
                        )}
                    </Box>
                ))}
            </Box>


            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button startIcon={<RestartAltIcon />} onClick={onResetAll} style={{ borderRadius: "10px", fontFamily: "montserrat" }}>Reset All</Button>
                <Button variant="contained" disabled={disabled} startIcon={<FilterAltIcon />} onClick={onApply} style={{ borderRadius: "10px", fontFamily: "montserrat" }}>Apply</Button>
            </Box>
        </Box>
    );
};

export default DrawerFilters;